import * as THREE from "three";
import type {
  SuntraceEnvironmentAssetId,
  SuntraceEnvironmentAssets,
} from "./environmentAssets";
import { SUNTRACE_ROCK_MOSS_PART_CONTRACTS } from "./environmentAssets";
import type { SuntraceQuality } from "./types";
import type { SuntraceTrack } from "./track";

type PlacementAlignment = "cape-terrace" | "terrain" | "track";

type AuthoredEnvironmentPlacement = {
  name: string;
  asset: SuntraceEnvironmentAssetId;
  progress: number;
  lateralOffset: number;
  groundOffset: number;
  yaw: number;
  tilt: readonly [number, number];
  scale: readonly [number, number, number];
  minimumQuality: SuntraceQuality;
  alignment: PlacementAlignment;
  castsShadow: boolean;
};

type AuthoredEnvironmentMesh = THREE.Mesh<
  THREE.BufferGeometry,
  THREE.Material | THREE.Material[]
>;

const isAuthoredEnvironmentMesh = (
  object: THREE.Object3D,
): object is AuthoredEnvironmentMesh => object instanceof THREE.Mesh;

export const CAPE_ASTER_WATERFRONT_GALLERY_ENVELOPE = {
  progressMin: 0.1704,
  progressMax: 0.1776,
  lateralMin: -34.01,
  lateralMax: -19.99,
  maximumHeight: 6.7,
  minimumRoadEdgeClearance: 9.75,
} as const;

export const CAPE_ASTER_WATERFRONT_GALLERY_PLACEMENT = {
  name: "Cape Aster waterfront service gallery",
  asset: "cape-waterfront-gallery",
  progress: 0.174,
  lateralOffset: -27,
  groundOffset: -0.88,
  yaw: 0.42,
  tilt: [0, 0],
  scale: [1, 1, 1],
  minimumQuality: "high",
  alignment: "cape-terrace",
  castsShadow: true,
} as const satisfies AuthoredEnvironmentPlacement;

export const CAPE_ASTER_MIRADOR_LANDMARK_PLACEMENT = {
  name: "Mirador saltwind beacon",
  asset: "boulder-lod",
  progress: 0.252,
  lateralOffset: 33,
  groundOffset: -0.24,
  yaw: -0.74,
  tilt: [-0.045, 0.035],
  scale: [4.8, 9, 4.2],
  minimumQuality: "medium",
  alignment: "terrain",
  castsShadow: true,
} as const satisfies AuthoredEnvironmentPlacement;

export const CAPE_ASTER_CAUSEWAY_DENSITY_CONTRACT = {
  startProgress: 0.115,
  endProgress: 0.17,
  cameraStartProgress: 0.095,
  maximumProgressGap: 0.024,
  minimumRoadEdgeClearance: 5.5,
  minimumRockHeight: 5.5,
  minimumRockFootprint: 6,
  minimumDominantRockHeight: 8.5,
  minimumDominantRockFootprint: 12,
  minimumVegetationHeight: 3.5,
  minimumVegetationFootprint: 6,
  maximumPlacements: 3,
  maximumSourceTriangles: 17_050,
  maximumShadowTriangles: 4_400,
  maximumRenderedTriangleContribution: 38_450,
  maximumDrawCalls: 3,
} as const;

export const CAPE_ASTER_CAUSEWAY_PLACEMENTS = [
  {
    name: "Causeway upper ochre outcrop",
    asset: "boulder-lod",
    progress: 0.132,
    lateralOffset: -21.8,
    groundOffset: -1.05,
    yaw: 0.62,
    tilt: [-0.06, 0.04],
    scale: [8.4, 9.1, 7.8],
    minimumQuality: "low",
    alignment: "cape-terrace",
    castsShadow: true,
  },
  {
    name: "Causeway roadside copper outcrop",
    asset: "boulder-lod",
    progress: 0.156,
    lateralOffset: -17.6,
    groundOffset: -0.95,
    yaw: 2.1,
    tilt: [0.045, -0.055],
    scale: [4.8, 7, 4.1],
    minimumQuality: "medium",
    alignment: "cape-terrace",
    castsShadow: false,
  },
  {
    name: "Causeway gallery saltwind grove",
    asset: "shrub-three",
    progress: 0.169,
    lateralOffset: -27.2,
    groundOffset: -0.08,
    yaw: 0.83,
    tilt: [0.008, -0.014],
    scale: [4.8, 9, 20],
    minimumQuality: "high",
    alignment: "cape-terrace",
    castsShadow: false,
  },
] as const satisfies readonly AuthoredEnvironmentPlacement[];

export const COASTAL_ROCK_TRIAL_CONTRACT = {
  maximumPlacements: 3,
  maximumSourceTriangles: 11_004,
  maximumRenderedTriangleContribution: 22_008,
  maximumDrawCalls: 3,
  maximumShadowTriangles: 0,
  minimumRoadEdgeClearance: 10.5,
  minimumSink: 0.14,
  maximumSink: 0.22,
} as const;

export const COASTAL_ROCK_TRIAL_PLACEMENTS = [
  {
    name: "Citrine moss saddle",
    asset: "rock-moss-10",
    progress: 0.347,
    lateralOffset: -22.5,
    groundOffset: -0.16,
    yaw: 0.54,
    tilt: [0, 0],
    scale: [2.85, 3.9, 2.9],
    minimumQuality: "high",
    alignment: "terrain",
    castsShadow: false,
  },
  {
    name: "Tempest moss shoulder",
    asset: "rock-moss-11",
    progress: 0.615,
    lateralOffset: 24.2,
    groundOffset: -0.18,
    yaw: -0.82,
    tilt: [0, 0],
    scale: [2.8, 2.75, 2.95],
    minimumQuality: "high",
    alignment: "terrain",
    castsShadow: false,
  },
  {
    name: "Aqueduct moss sentinel",
    asset: "rock-moss-13",
    progress: 0.838,
    lateralOffset: -25.2,
    groundOffset: -0.22,
    yaw: 1.17,
    tilt: [0, 0],
    scale: [3.05, 2.9, 3.25],
    minimumQuality: "high",
    alignment: "terrain",
    castsShadow: false,
  },
] as const satisfies readonly AuthoredEnvironmentPlacement[];

export const SUNTRACE_HIGH_FIDELITY_AUTHORED_CONTRACT = Object.freeze({
  maximumPlacements: 6,
  maximumSourceTriangles: 98_000,
  maximumDrawCalls: 6,
  maximumShadowTriangles: 0,
  minimumRoadEdgeClearance: 8,
});

export const SUNTRACE_HIGH_FIDELITY_AUTHORED_PLACEMENTS = [
  {
    name: "Mirador wind-cut shoulder",
    asset: "shrub-three",
    progress: 0.218,
    lateralOffset: 19.5,
    groundOffset: 0.02,
    yaw: -0.46,
    tilt: [0, 0],
    scale: [3.2, 3.4, 3.1],
    minimumQuality: "high",
    alignment: "terrain",
    castsShadow: false,
  },
  {
    name: "Citrine weathered spur",
    asset: "rock-face-one",
    progress: 0.332,
    lateralOffset: -31,
    groundOffset: -0.72,
    yaw: 0.38,
    tilt: [-0.018, 0.042],
    scale: [1.2, 1.3, 1.1],
    minimumQuality: "high",
    alignment: "terrain",
    castsShadow: false,
  },
  {
    name: "Tempest fractured shelf",
    asset: "rock-face-two",
    progress: 0.458,
    lateralOffset: 29,
    groundOffset: -0.66,
    yaw: -0.52,
    tilt: [0.026, -0.038],
    scale: [1.35, 1.42, 1.2],
    minimumQuality: "high",
    alignment: "terrain",
    castsShadow: false,
  },
  {
    name: "Solarium saltwind thicket",
    asset: "shrub-two",
    progress: 0.574,
    lateralOffset: -21,
    groundOffset: 0.03,
    yaw: 0.71,
    tilt: [0.012, -0.008],
    scale: [0.72, 0.82, 0.76],
    minimumQuality: "high",
    alignment: "terrain",
    castsShadow: false,
  },
  {
    name: "Aqueduct dry seed bank",
    asset: "shrub-three",
    progress: 0.684,
    lateralOffset: 19.5,
    groundOffset: 0.02,
    yaw: 0.29,
    tilt: [0, 0],
    scale: [3.1, 3.25, 3],
    minimumQuality: "high",
    alignment: "terrain",
    castsShadow: false,
  },
  {
    name: "Meridian eroded marker",
    asset: "boulder-lod",
    progress: 0.932,
    lateralOffset: -20,
    groundOffset: -0.2,
    yaw: 1.08,
    tilt: [-0.045, 0.028],
    scale: [3.8, 4.3, 3.5],
    minimumQuality: "high",
    alignment: "terrain",
    castsShadow: false,
  },
] as const satisfies readonly AuthoredEnvironmentPlacement[];

const authoredPlacements: readonly AuthoredEnvironmentPlacement[] = [
  ...SUNTRACE_HIGH_FIDELITY_AUTHORED_PLACEMENTS,
  {
    name: "Aster shelf face",
    asset: "rock-face-one",
    progress: 0.031,
    lateralOffset: 65,
    groundOffset: -1.2,
    yaw: -0.62,
    tilt: [-0.025, 0.065],
    scale: [2.1, 2.2, 1.8],
    minimumQuality: "low",
    alignment: "terrain",
    castsShadow: true,
  },
  {
    name: "Aurelia lower fault",
    asset: "rock-face-one",
    progress: 0.056,
    lateralOffset: 48,
    groundOffset: -1,
    yaw: -0.45,
    tilt: [0.025, 0.055],
    scale: [2.3, 2.2, 1.9],
    minimumQuality: "low",
    alignment: "terrain",
    castsShadow: true,
  },
  {
    name: "Aurelia middle fault",
    asset: "rock-face-two",
    progress: 0.067,
    lateralOffset: 55,
    groundOffset: -1.25,
    yaw: 0.34,
    tilt: [0.025, -0.06],
    scale: [2.7, 2.6, 2.2],
    minimumQuality: "medium",
    alignment: "terrain",
    castsShadow: true,
  },
  {
    name: "Aurelia upper fault",
    asset: "rock-face-one",
    progress: 0.079,
    lateralOffset: 62,
    groundOffset: -1.45,
    yaw: 0.55,
    tilt: [-0.02, -0.075],
    scale: [3.1, 3, 2.5],
    minimumQuality: "high",
    alignment: "terrain",
    castsShadow: true,
  },
  {
    name: "Copper crown boulder",
    asset: "boulder",
    progress: 0.094,
    lateralOffset: 20,
    groundOffset: -0.6,
    yaw: 0.62,
    tilt: [-0.08, 0.045],
    scale: [6.3, 5.4, 5.85],
    minimumQuality: "medium",
    alignment: "terrain",
    castsShadow: true,
  },
  {
    name: "Cape lower shelf",
    asset: "rock-face-one",
    progress: 0.112,
    lateralOffset: -30.5,
    groundOffset: -1.05,
    yaw: -0.64,
    tilt: [0.015, -0.075],
    scale: [2.5, 2.25, 2.25],
    minimumQuality: "high",
    alignment: "terrain",
    castsShadow: true,
  },
  {
    name: "Cape return face",
    asset: "rock-face-two",
    progress: 0.121,
    lateralOffset: -37.5,
    groundOffset: -0.9,
    yaw: -0.42,
    tilt: [-0.02, 0.06],
    scale: [2.8, 2.65, 2.5],
    minimumQuality: "medium",
    alignment: "terrain",
    castsShadow: true,
  },
  {
    name: "Cape crown stone",
    asset: "boulder",
    progress: 0.13,
    lateralOffset: -44.5,
    groundOffset: -0.25,
    yaw: 1.02,
    tilt: [-0.055, 0.04],
    scale: [5.4, 4.6, 5.1],
    minimumQuality: "high",
    alignment: "terrain",
    castsShadow: true,
  },
  {
    name: "Cape lower footstone",
    asset: "boulder",
    progress: 0.115,
    lateralOffset: -29,
    groundOffset: -0.17,
    yaw: 2.18,
    tilt: [0.04, -0.035],
    scale: [3.6, 2.8, 3.4],
    minimumQuality: "medium",
    alignment: "terrain",
    castsShadow: true,
  },
  {
    name: "Cape return footstone",
    asset: "boulder",
    progress: 0.126,
    lateralOffset: -35.5,
    groundOffset: -0.13,
    yaw: 0.36,
    tilt: [-0.035, 0.03],
    scale: [3.2, 2.5, 3],
    minimumQuality: "high",
    alignment: "terrain",
    castsShadow: true,
  },
  ...CAPE_ASTER_CAUSEWAY_PLACEMENTS,
  CAPE_ASTER_WATERFRONT_GALLERY_PLACEMENT,
  CAPE_ASTER_MIRADOR_LANDMARK_PLACEMENT,
  ...COASTAL_ROCK_TRIAL_PLACEMENTS,
  {
    name: "Saltwind shrub bank one",
    asset: "shrub-two",
    progress: 0.014,
    lateralOffset: 13.8,
    groundOffset: 0.04,
    yaw: 0.4,
    tilt: [0, 0.02],
    scale: [0.86, 0.9, 0.86],
    minimumQuality: "medium",
    alignment: "track",
    castsShadow: false,
  },
  {
    name: "Saltwind shrub bank two",
    asset: "shrub-two",
    progress: 0.044,
    lateralOffset: 14.1,
    groundOffset: 0.03,
    yaw: -0.72,
    tilt: [0.015, -0.01],
    scale: [0.96, 1, 0.96],
    minimumQuality: "low",
    alignment: "track",
    castsShadow: false,
  },
  {
    name: "Saltwind shrub bank three",
    asset: "shrub-two",
    progress: 0.089,
    lateralOffset: 14.4,
    groundOffset: 0.04,
    yaw: 0.96,
    tilt: [-0.02, 0.015],
    scale: [1.02, 1.06, 1.02],
    minimumQuality: "medium",
    alignment: "track",
    castsShadow: false,
  },
  {
    name: "Saltwind shrub bank four",
    asset: "shrub-two",
    progress: 0.116,
    lateralOffset: -14.2,
    groundOffset: 0.04,
    yaw: -0.35,
    tilt: [0.02, 0.01],
    scale: [0.92, 0.96, 0.92],
    minimumQuality: "high",
    alignment: "track",
    castsShadow: false,
  },
  {
    name: "Roadside seed heads one",
    asset: "shrub-three",
    progress: 0.026,
    lateralOffset: -12.4,
    groundOffset: 0.05,
    yaw: -0.55,
    tilt: [0, 0],
    scale: [2.5, 2.25, 2.4],
    minimumQuality: "medium",
    alignment: "track",
    castsShadow: false,
  },
  {
    name: "Roadside seed heads two",
    asset: "shrub-three",
    progress: 0.07,
    lateralOffset: 12.5,
    groundOffset: 0.05,
    yaw: 0.82,
    tilt: [0, 0],
    scale: [2.7, 2.4, 2.55],
    minimumQuality: "low",
    alignment: "track",
    castsShadow: false,
  },
  {
    name: "Roadside seed heads three",
    asset: "shrub-three",
    progress: 0.102,
    lateralOffset: 12.7,
    groundOffset: 0.05,
    yaw: 0.15,
    tilt: [0, 0],
    scale: [2.35, 2.15, 2.3],
    minimumQuality: "high",
    alignment: "track",
    castsShadow: false,
  },
  {
    name: "Mirador fractured wall",
    asset: "rock-face-two",
    progress: 0.246,
    lateralOffset: -52,
    groundOffset: -1.3,
    yaw: -0.42,
    tilt: [0.035, 0.06],
    scale: [3.2, 3.1, 2.6],
    minimumQuality: "low",
    alignment: "terrain",
    castsShadow: true,
  },
  {
    name: "Mirador copper boulder",
    asset: "boulder",
    progress: 0.286,
    lateralOffset: 26,
    groundOffset: -0.8,
    yaw: 1.12,
    tilt: [-0.06, 0.05],
    scale: [5.8, 5.1, 5.6],
    minimumQuality: "medium",
    alignment: "terrain",
    castsShadow: true,
  },
  {
    name: "Citrine ledge",
    asset: "rock-face-one",
    progress: 0.408,
    lateralOffset: 52,
    groundOffset: -1.25,
    yaw: 0.31,
    tilt: [-0.02, -0.065],
    scale: [3.2, 3.3, 2.6],
    minimumQuality: "medium",
    alignment: "terrain",
    castsShadow: true,
  },
  {
    name: "Tempest gate boulder",
    asset: "boulder",
    progress: 0.528,
    lateralOffset: -28,
    groundOffset: -0.9,
    yaw: 2.1,
    tilt: [0.045, -0.055],
    scale: [5.8, 5.2, 5.55],
    minimumQuality: "high",
    alignment: "terrain",
    castsShadow: true,
  },
  {
    name: "Solarium fault wall",
    asset: "rock-face-two",
    progress: 0.692,
    lateralOffset: 54,
    groundOffset: -1.35,
    yaw: 0.39,
    tilt: [0.02, -0.075],
    scale: [3.3, 3.2, 2.65],
    minimumQuality: "low",
    alignment: "terrain",
    castsShadow: true,
  },
  {
    name: "Aqueduct shelf",
    asset: "rock-face-one",
    progress: 0.786,
    lateralOffset: -53,
    groundOffset: -1.3,
    yaw: -0.36,
    tilt: [-0.025, 0.07],
    scale: [3.2, 3.25, 2.6],
    minimumQuality: "medium",
    alignment: "terrain",
    castsShadow: true,
  },
  {
    name: "Helios copper boulder",
    asset: "boulder",
    progress: 0.874,
    lateralOffset: 27,
    groundOffset: -0.8,
    yaw: 0.44,
    tilt: [-0.07, 0.04],
    scale: [6, 5.2, 5.7],
    minimumQuality: "high",
    alignment: "terrain",
    castsShadow: true,
  },
  {
    name: "Meridian return wall",
    asset: "rock-face-two",
    progress: 0.944,
    lateralOffset: -54,
    groundOffset: -1.35,
    yaw: -0.4,
    tilt: [0.03, 0.075],
    scale: [3.3, 3.2, 2.65],
    minimumQuality: "medium",
    alignment: "terrain",
    castsShadow: true,
  },
  {
    name: "Mirador saltwind bank",
    asset: "shrub-two",
    progress: 0.267,
    lateralOffset: -14.2,
    groundOffset: 0.04,
    yaw: 0.52,
    tilt: [0.015, -0.01],
    scale: [1, 1.04, 1],
    minimumQuality: "medium",
    alignment: "track",
    castsShadow: false,
  },
  {
    name: "Citrine saltwind bank",
    asset: "shrub-two",
    progress: 0.384,
    lateralOffset: 14.3,
    groundOffset: 0.04,
    yaw: -0.61,
    tilt: [-0.015, 0.01],
    scale: [1.03, 1.08, 1.03],
    minimumQuality: "low",
    alignment: "track",
    castsShadow: false,
  },
  {
    name: "Tempest seed heads",
    asset: "shrub-three",
    progress: 0.548,
    lateralOffset: -12.8,
    groundOffset: 0.05,
    yaw: 0.78,
    tilt: [0, 0],
    scale: [2.65, 2.35, 2.5],
    minimumQuality: "medium",
    alignment: "track",
    castsShadow: false,
  },
  {
    name: "Solarium saltwind bank",
    asset: "shrub-two",
    progress: 0.718,
    lateralOffset: 14.2,
    groundOffset: 0.04,
    yaw: 0.91,
    tilt: [0.01, 0.015],
    scale: [1, 1.05, 1],
    minimumQuality: "low",
    alignment: "track",
    castsShadow: false,
  },
  {
    name: "Aqueduct seed heads",
    asset: "shrub-three",
    progress: 0.805,
    lateralOffset: -12.5,
    groundOffset: 0.05,
    yaw: -0.45,
    tilt: [0, 0],
    scale: [2.55, 2.3, 2.45],
    minimumQuality: "high",
    alignment: "track",
    castsShadow: false,
  },
  {
    name: "Helios saltwind bank",
    asset: "shrub-two",
    progress: 0.892,
    lateralOffset: 14.4,
    groundOffset: 0.04,
    yaw: -0.73,
    tilt: [-0.015, 0],
    scale: [1.03, 1.08, 1.03],
    minimumQuality: "medium",
    alignment: "track",
    castsShadow: false,
  },
  {
    name: "Meridian seed heads",
    asset: "shrub-three",
    progress: 0.963,
    lateralOffset: -12.7,
    groundOffset: 0.05,
    yaw: 0.33,
    tilt: [0, 0],
    scale: [2.7, 2.4, 2.55],
    minimumQuality: "medium",
    alignment: "track",
    castsShadow: false,
  },
];

const qualityRanks: Record<SuntraceQuality, number> = {
  low: 0,
  medium: 1,
  high: 2,
};

export const SUNTRACE_HIGH_ROOT_VISIBILITY_RANGE = 0.2;

export type SuntraceAuthoredDensity = Record<SuntraceQuality, number> & {
  total: number;
};

const getWrappedProgressDistance = (first: number, second: number): number =>
  Math.abs(((first - second + 1.5) % 1) - 0.5);

const getCapeTerracePlacementHeight = (
  frame: ReturnType<SuntraceTrack["sample"]>,
  lateralOffset: number,
  getTerrainHeight: (x: number, z: number) => number,
): number => {
  const position = frame.center
    .clone()
    .addScaledVector(frame.right, lateralOffset);
  const naturalHeight = getTerrainHeight(position.x, position.z) + 0.16;
  const distanceFromRoad = Math.abs(lateralOffset);
  const innerTerrace = THREE.MathUtils.smoothstep(
    distanceFromRoad,
    15.5,
    24.5,
  );
  const outerTerrace = THREE.MathUtils.smoothstep(
    distanceFromRoad,
    24.5,
    36,
  );
  const upperLevel = THREE.MathUtils.lerp(1.12, 0.42, innerTerrace);
  const sculptedDrop = THREE.MathUtils.lerp(
    upperLevel,
    -5.1,
    outerTerrace,
  );
  return Math.max(naturalHeight, frame.center.y + sculptedDrop);
};

export type SuntraceAuthoredEnvironment = {
  group: THREE.Group;
  getActiveDensity: () => SuntraceAuthoredDensity;
  getWeatheringEnabled: () => boolean;
  updateVisibility: (trackProgress: number) => void;
  setQuality: (quality: SuntraceQuality) => void;
  dispose: () => void;
};

export const createSuntraceAuthoredEnvironment = (
  track: SuntraceTrack,
  assets: SuntraceEnvironmentAssets,
  initialQuality: SuntraceQuality,
  getTerrainHeight: (x: number, z: number) => number,
  minimumPlacementHeight = Number.NEGATIVE_INFINITY,
): SuntraceAuthoredEnvironment => {
  const group = new THREE.Group();
  group.name = "Aurelia authored coastal bend";
  const qualityGroups = new Map<SuntraceQuality, THREE.Group>();
  const placementRoots: {
    minimumQuality: SuntraceQuality;
    progress: number;
    object: THREE.Object3D;
  }[] = [];
  const highQualityRoots: {
    progress: number;
    object: THREE.Object3D;
  }[] = [];
  for (const quality of ["low", "medium", "high"] as const) {
    const qualityGroup = new THREE.Group();
    qualityGroup.name = `${quality} authored scenery`;
    qualityGroups.set(quality, qualityGroup);
    group.add(qualityGroup);
  }
  const frame = track.sample(0);
  const position = new THREE.Vector3();
  for (const placement of authoredPlacements) {
    if (placement.alignment === "track") {
      track.sample(placement.progress, 0, frame);
    } else {
      track.samplePlacement(placement.progress, 0, frame);
    }
    const object = assets.clone(placement.asset);
    object.name = placement.name;
    position
      .copy(frame.center)
      .addScaledVector(frame.right, placement.lateralOffset);
    if (placement.alignment === "track") {
      position.addScaledVector(frame.normal, placement.groundOffset);
      object.quaternion.copy(frame.quaternion);
      object.rotateY(placement.yaw);
      object.rotateX(placement.tilt[0]);
      object.rotateZ(placement.tilt[1]);
    } else {
      position.y = placement.alignment === "cape-terrace"
        ? getCapeTerracePlacementHeight(
            frame,
            placement.lateralOffset,
            getTerrainHeight,
          ) + placement.groundOffset
        : getTerrainHeight(position.x, position.z) + placement.groundOffset;
      const heading = Math.atan2(frame.tangent.x, frame.tangent.z);
      object.rotation.set(
        placement.tilt[0],
        heading + placement.yaw,
        placement.tilt[1],
      );
    }
    object.position.copy(position);
    if (object.position.y < minimumPlacementHeight) {
      continue;
    }
    object.scale.set(...placement.scale);
    object.traverse((child) => {
      if (isAuthoredEnvironmentMesh(child)) {
        child.castShadow = placement.castsShadow;
        child.receiveShadow = true;
        if (placement.asset in SUNTRACE_ROCK_MOSS_PART_CONTRACTS) {
          const materials = Array.isArray(child.material)
            ? child.material
            : [child.material];
          for (const material of materials) {
            material.side = THREE.FrontSide;
            material.needsUpdate = true;
          }
        }
      }
    });
    qualityGroups.get(placement.minimumQuality)?.add(object);
    placementRoots.push({
      minimumQuality: placement.minimumQuality,
      progress: placement.progress,
      object,
    });
    if (placement.minimumQuality === "high") {
      highQualityRoots.push({ progress: placement.progress, object });
    }
  }
  const setQuality = (quality: SuntraceQuality): void => {
    assets.setQuality(quality);
    const activeRank = qualityRanks[quality];
    for (const [minimumQuality, qualityGroup] of qualityGroups) {
      qualityGroup.visible = qualityRanks[minimumQuality] <= activeRank;
    }
  };
  const updateVisibility = (trackProgress: number): void => {
    for (const root of highQualityRoots) {
      root.object.visible =
        getWrappedProgressDistance(root.progress, trackProgress) <=
        SUNTRACE_HIGH_ROOT_VISIBILITY_RANGE + Number.EPSILON * 8;
    }
  };
  const getActiveDensity = (): SuntraceAuthoredDensity => {
    const density: SuntraceAuthoredDensity = {
      low: 0,
      medium: 0,
      high: 0,
      total: 0,
    };
    for (const root of placementRoots) {
      const qualityGroup = qualityGroups.get(root.minimumQuality);
      if (qualityGroup?.visible !== true || !root.object.visible) {
        continue;
      }
      density[root.minimumQuality] += 1;
      density.total += 1;
    }
    return density;
  };
  setQuality(initialQuality);
  updateVisibility(0);
  return {
    group,
    getActiveDensity,
    getWeatheringEnabled: assets.getWeatheringEnabled,
    updateVisibility,
    setQuality,
    dispose() {
      group.removeFromParent();
      group.clear();
      qualityGroups.clear();
      placementRoots.length = 0;
      highQualityRoots.length = 0;
    },
  };
};
